import React, { useContext } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Chip, Typography } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import { GlobalContext } from "../../contexts/GlobalContext"
import getIconComponent from "../utils/getIconComponent"
import { PlayCircleOutline } from "@mui/icons-material"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

// Utilidad para mapear los tipos de encuesta
const getTipoEncuestaLabel = (tipo) => {
  switch (tipo) {
    case "RAP":
      return "Rápida"
    case "SAT":
      return "Satisfacción"
    case "PON":
      return "Ponderación"
    default:
      return tipo
  }
}

const EjecEncuestasIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box
          sx={{
            textAlign: "center",
            mb: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#196C87",
          }}
        >
          <PlayCircleOutline sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Ejecución de Encuestas Disponibles
          </Typography>
        </Box>

        <Box className={StyledRoot}>
          {/* GRILLA PRINCIPAL */}
          <CustomConditionalActionsTableServer
            endpoint="/EjecEncuestas/getEncuestasDisponibles" // ⭐ Apunta al endpoint proxy del usuario logueado
            errorMsgFilterSearch="Error al cargar la bandeja de encuestas para ejecución"
            queryKeyModal="EjecEncuestas_Bandeja_Principal"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              // Validamos permisos para el botón EJECUTAR / RESPONDER
              const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) =>
                  action?.acccaption === "EJECUTAR" ||
                  action?.acccaption === "RESPONDER" ||
                  action?.acccaption === "GESTIONAR",
              )

              const actions = []
              if (ejecutarAction) {
                actions.push({
                  label: "Iniciar Encuesta",
                  key: "ejecutar",
                  icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
                  onClick: (row) => {
                    // Enviamos la fila seleccionada a la pantalla del formulario
                    navigate("ejecutar", { state: { encuesta: row.original } })
                  },
                })
              }
              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              // Validamos permisos para EXPORTAR en la barra superior
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )

              const toolbarActions = []

              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      onClick: ({ columns, data }) => {
                        const title = "Encuestas Disponibles"
                        device === "sm"
                          ? handleExportDataPdfSMScreen(columns, data, title, title)
                          : handleExportDataPdfLGScreen(columns, table.getCoreRowModel().rows, title, title)
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      onClick: ({ data }) => handleAllExportDataCSV(data, "Encuestas_Disponibles_SIAC"),
                    },
                  ],
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "enccodigo",
                header: "Código",
                size: 130,
                Cell: ({ cell }) => <span style={{ fontWeight: "bold", color: "#196C87" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "enctitulo",
                header: "Título de la Encuesta",
                size: 300,
                Cell: ({ cell }) => <span style={{ fontWeight: "500" }}>{cell.getValue()}</span>,
              },
              {
                accessorKey: "enctipo",
                header: "Tipo",
                size: 130,
                Cell: ({ cell }) => (
                  <Chip
                    label={getTipoEncuestaLabel(cell.getValue())}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: "bold" }}
                  />
                ),
              },
              {
                accessorKey: "encpublica",
                header: "Modalidad",
                size: 140,
                Cell: ({ cell }) => {
                  const esPublica = cell.getValue() === 1
                  return (
                    <Chip
                      label={esPublica ? "PÚBLICA" : "ASIGNADA"}
                      color={esPublica ? "success" : "info"}
                      size="small"
                      variant={esPublica ? "filled" : "outlined"}
                    />
                  )
                },
              },
              {
                accessorKey: "encdescri",
                header: "Descripción",
                size: 350,
                Cell: ({ cell }) => {
                  const desc = cell.getValue()
                  return desc ? (
                    <span style={{ fontSize: "0.85rem", color: "#555" }}>{desc}</span>
                  ) : (
                    <i>Sin descripción</i>
                  )
                },
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EjecEncuestasIndex
